import { API_BASE_URL } from '@/config';
import { type FieldErrors, normalizeValidationDetails } from '@/lib/validationErrors';

export type ApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'validation'
  | 'not_found'
  | 'server'
  | 'network'
  | 'unknown';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  /** Optional diagnostic detail surfaced by some endpoints on error. */
  detail?: string;
  status?: number;
  errorKind?: ApiErrorKind;
  requestId?: string;
  details?: FieldErrors;
  code?: 'subscription_required' | 'gym_not_active' | string;
  subscription_status?: string;
  subscription_source?: 'self' | 'gym';
  checkout_url?: string;
}

export interface AffiliateCheckoutSession {
  checkout_url: string;
  session_id: string;
  affiliate: { id: string; name: string };
}

interface RequestConfig extends RequestInit {
  skipAuth?: boolean;
  idempotencyRecoveryAttempted?: boolean;
}

type TokenGetter = () => Promise<string | null>;

export interface ApiParsedResponseContext {
  endpoint: string;
  response: Response;
  body: unknown;
}

export interface ApiClientOptions {
  failureMetadata?: 'detail' | 'subscription';
  onParsedResponse?: (context: ApiParsedResponseContext) => void;
}

type ApiErrorOptions = {
  kind?: ApiErrorKind;
  status?: number;
  requestId?: string;
  details?: FieldErrors;
};

type ApiFailureBody = {
  error?: string;
  message?: string;
  detail?: string;
  code?: string;
  subscription_status?: string;
  subscription_source?: 'self' | 'gym';
  checkout_url?: string;
  details?: FieldErrors;
};

type ApiJsonBodyResult = { ok: true; body: unknown } | { ok: false; error: string };

const API_REQUEST_TIMEOUT_MS = 15_000;

export class IdempotencyKeyRecovery {
  private readonly aliases = new Map<string, string>();

  private resolveKey(key: string): string {
    const visited = new Set<string>();
    let resolved = key;

    while (!visited.has(resolved)) {
      visited.add(resolved);
      const next = this.aliases.get(resolved);
      if (!next) break;
      resolved = next;
    }

    for (const alias of visited) {
      if (alias !== resolved) this.aliases.set(alias, resolved);
    }
    return resolved;
  }

  prepareHeaders(headers?: HeadersInit): Headers {
    const prepared = new Headers(headers);
    const idempotencyKey = prepared.get('Idempotency-Key');
    if (idempotencyKey) prepared.set('Idempotency-Key', this.resolveKey(idempotencyKey));
    return prepared;
  }

  rotateHeaders(headers?: HeadersInit): Headers | null {
    const prepared = this.prepareHeaders(headers);
    const currentKey = prepared.get('Idempotency-Key');
    if (!currentKey) return null;

    const rotatedKey = crypto.randomUUID();
    this.aliases.set(currentKey, rotatedKey);
    prepared.set('Idempotency-Key', rotatedKey);
    return prepared;
  }
}

export function getAutomaticApiRequestTimeout(method = 'GET'): number | null {
  return ['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase()) ? API_REQUEST_TIMEOUT_MS : null;
}

export function createApiRequestSignal(
  callerSignal?: AbortSignal,
  timeoutMs: number | null = API_REQUEST_TIMEOUT_MS
) {
  const controller = new AbortController();
  let timedOut = false;
  const onCallerAbort = () => controller.abort(callerSignal?.reason);
  const timeout =
    timeoutMs === null
      ? undefined
      : setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, timeoutMs);

  if (callerSignal?.aborted) {
    onCallerAbort();
  } else {
    callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
  }

  return {
    signal: controller.signal,
    didTimeout: () => timedOut,
    cleanup: () => {
      if (timeout !== undefined) clearTimeout(timeout);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    },
  };
}

export function waitForApiRequest<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) {
    return Promise.reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
  }

  return new Promise((resolve, reject) => {
    const onAbort = () => reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      }
    );
  });
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status?: number;
  readonly requestId?: string;
  readonly details?: FieldErrors;

  constructor(message: string, options: ApiErrorOptions = {}) {
    super(message);
    this.name = 'ApiError';
    this.kind = options.kind ?? 'unknown';
    this.status = options.status;
    this.requestId = options.requestId;
    this.details = options.details;
  }
}

export function classifyApiError(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 400 || status === 409 || status === 422) return 'validation';
  if (status === 404) return 'not_found';
  if (status >= 500) return 'server';
  return 'unknown';
}

export function getApiDiagnosticEndpoint(endpoint: string): string {
  try {
    return new URL(endpoint, 'https://ergshoot.invalid').pathname
      .split('/')
      .map((segment) => {
        const decoded = decodeURIComponent(segment);
        const isLikelyIdentifier =
          decoded.includes('@') ||
          /^\d+$/.test(decoded) ||
          /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(decoded) ||
          /^territory-[a-z0-9-]+$/i.test(decoded) ||
          /^(?:acct|agent|aff|cs|cus|event|in|member|org|pi|pm|price|prod|profile|score|sub|user|workout)_[a-z0-9_-]+$/i.test(
            decoded
          ) ||
          (decoded.length >= 20 && /[a-z]/i.test(decoded) && /\d/.test(decoded));
        return isLikelyIdentifier ? ':id' : segment;
      })
      .join('/');
  } catch {
    return 'unknown';
  }
}

export function toApiError<T>(response: ApiResponse<T>, fallback = 'Request failed'): ApiError {
  return new ApiError(response.error || response.message || fallback, {
    kind: response.errorKind,
    status: response.status,
    requestId: response.requestId,
    details: response.details,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readOptionalString(body: Record<string, unknown>, key: string) {
  const value = body[key];
  return typeof value === 'string' ? value : undefined;
}

export async function readApiJsonBody(response: Response): Promise<ApiJsonBodyResult> {
  try {
    return { ok: true, body: await response.json() };
  } catch (error) {
    if (!(error instanceof SyntaxError)) throw error;
    return { ok: false, error: 'Invalid API response' };
  }
}

export function extractApiFailureBody(body: unknown): ApiFailureBody {
  if (!isRecord(body)) return {};

  const subscriptionSource = readOptionalString(body, 'subscription_source');

  return {
    error: readOptionalString(body, 'error'),
    message: readOptionalString(body, 'message'),
    detail: readOptionalString(body, 'detail'),
    code: readOptionalString(body, 'code'),
    subscription_status: readOptionalString(body, 'subscription_status'),
    subscription_source:
      subscriptionSource === 'self' || subscriptionSource === 'gym'
        ? subscriptionSource
        : undefined,
    checkout_url: readOptionalString(body, 'checkout_url'),
    details: normalizeValidationDetails(body.details),
  };
}

export function getApiErrorPresentation(error: unknown) {
  const kind = error instanceof ApiError ? error.kind : 'unknown';
  const presentations: Record<
    ApiErrorKind,
    { title: string; description: string; retryLabel: string | null }
  > = {
    unauthorized: {
      title: 'Your session needs attention',
      description: 'Please try again. If the problem continues, sign out and sign back in.',
      retryLabel: 'Try again',
    },
    forbidden: {
      title: "You don't have access",
      description: 'Switch to a profile with access or contact your administrator.',
      retryLabel: null,
    },
    validation: {
      title: 'Check your information',
      description:
        'Some information could not be accepted. Review the request before trying again.',
      retryLabel: null,
    },
    not_found: {
      title: "We couldn't find that",
      description: 'It may have moved or no longer be available.',
      retryLabel: null,
    },
    server: {
      title: 'ErgShoot is having trouble',
      description: 'Something went wrong on our side. Please wait a moment and try again.',
      retryLabel: 'Try again',
    },
    network: {
      title: 'Connection problem',
      description: 'Check your internet connection, then try again.',
      retryLabel: 'Try again',
    },
    unknown: {
      title: 'Something went wrong',
      description: 'Please try again. If the problem continues, contact ErgShoot support.',
      retryLabel: 'Try again',
    },
  };
  return presentations[kind];
}

class ApiClient {
  private baseUrl: string;
  private tokenGetter: TokenGetter | null;
  private readonly options: ApiClientOptions;
  private readonly idempotencyKeyRecovery = new IdempotencyKeyRecovery();

  constructor(baseUrl: string, tokenGetter?: TokenGetter, options: ApiClientOptions = {}) {
    this.baseUrl = baseUrl;
    this.tokenGetter = tokenGetter || null;
    this.options = options;
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    // Use provided token getter if available (from useApi hook)
    if (this.tokenGetter) {
      const token = await this.tokenGetter();
      if (token) {
        return { Authorization: `Bearer ${token}` };
      }
      return {};
    }

    // Fallback to window.Clerk for legacy usage (less reliable)
    const token = await window.Clerk?.session?.getToken();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }

  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const {
      skipAuth = false,
      idempotencyRecoveryAttempted = false,
      signal: callerSignal,
      ...fetchConfig
    } = config;
    const url = `${this.baseUrl}${endpoint}`;
    const requestSignal = createApiRequestSignal(
      callerSignal ?? undefined,
      getAutomaticApiRequestTimeout(fetchConfig.method)
    );

    try {
      const authHeaders = skipAuth
        ? {}
        : await waitForApiRequest(this.getAuthHeaders(), requestSignal.signal);
      const requestHeaders = this.idempotencyKeyRecovery.prepareHeaders(fetchConfig.headers);
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...Object.fromEntries(requestHeaders.entries()),
      };

      const response = await fetch(url, {
        ...fetchConfig,
        headers,
        signal: requestSignal.signal,
      });

      if (
        response.headers.get('Idempotency-Key-Rotation-Required') === 'true' &&
        !idempotencyRecoveryAttempted
      ) {
        const retryHeaders = this.idempotencyKeyRecovery.rotateHeaders(requestHeaders);
        if (retryHeaders) {
          return this.request<T>(endpoint, {
            ...fetchConfig,
            skipAuth,
            idempotencyRecoveryAttempted: true,
            signal: callerSignal,
            headers: Object.fromEntries(retryHeaders.entries()),
          });
        }
      }

      const parsedBody = await readApiJsonBody(response);
      if (parsedBody.ok) {
        this.options.onParsedResponse?.({
          endpoint,
          response,
          body: parsedBody.body,
        });
      }

      if (!response.ok) {
        const data = extractApiFailureBody(parsedBody.ok ? parsedBody.body : null);
        const requestId = response.headers.get('X-Request-ID') || undefined;
        const errorKind = classifyApiError(response.status);
        console.error('[ApiClient] Request failed', {
          method: fetchConfig.method || 'GET',
          endpoint: getApiDiagnosticEndpoint(endpoint),
          status: response.status,
          errorKind,
          requestId,
        });
        const failureMetadata =
          this.options.failureMetadata === 'subscription'
            ? {
                code: data.code,
                subscription_status: data.subscription_status,
                subscription_source: data.subscription_source,
                checkout_url: data.checkout_url,
              }
            : data.detail
              ? { detail: data.detail }
              : {};
        return {
          success: false,
          error: data.error || data.message || `Request failed with status ${response.status}`,
          ...failureMetadata,
          details: data.details,
          status: response.status,
          errorKind,
          requestId,
        };
      }

      if (!parsedBody.ok) {
        const requestId = response.headers.get('X-Request-ID') || undefined;
        console.error('[ApiClient] Request failed', {
          method: fetchConfig.method || 'GET',
          endpoint: getApiDiagnosticEndpoint(endpoint),
          status: response.status,
          errorKind: 'unknown',
          requestId,
        });
        return {
          success: false,
          error: parsedBody.error,
          status: response.status,
          errorKind: 'unknown',
          requestId,
        };
      }

      return parsedBody.body as ApiResponse<T>;
    } catch (error) {
      const message = requestSignal.didTimeout() ? 'Request timed out' : 'Network request failed';
      console.error('[ApiClient] Request failed', {
        method: fetchConfig.method || 'GET',
        endpoint: getApiDiagnosticEndpoint(endpoint),
        errorKind: 'network',
        errorType: error instanceof Error ? error.name : 'Error',
      });
      return { success: false, error: message, errorKind: 'network' };
    } finally {
      requestSignal.cleanup();
    }
  }

  // Convenience methods
  async get<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const requestSignal = createApiRequestSignal(undefined, null);

    try {
      const authHeaders = await waitForApiRequest(this.getAuthHeaders(), requestSignal.signal);
      const response = await fetch(url, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
        signal: requestSignal.signal,
      });

      const parsedBody = await readApiJsonBody(response);

      if (!response.ok) {
        const data = extractApiFailureBody(parsedBody.ok ? parsedBody.body : null);
        const requestId = response.headers.get('X-Request-ID') || undefined;
        const errorKind = classifyApiError(response.status);
        console.error('[ApiClient] Request failed', {
          method: 'POST',
          endpoint: getApiDiagnosticEndpoint(endpoint),
          status: response.status,
          errorKind,
          requestId,
        });
        return {
          success: false,
          error: data.error || data.message || `Upload failed with status ${response.status}`,
          status: response.status,
          errorKind,
          requestId,
          details: data.details,
        };
      }

      if (!parsedBody.ok) {
        const requestId = response.headers.get('X-Request-ID') || undefined;
        console.error('[ApiClient] Request failed', {
          method: 'POST',
          endpoint: getApiDiagnosticEndpoint(endpoint),
          status: response.status,
          errorKind: 'unknown',
          requestId,
        });
        return {
          success: false,
          error: parsedBody.error,
          status: response.status,
          errorKind: 'unknown',
          requestId,
        };
      }

      return parsedBody.body as ApiResponse<T>;
    } catch (error) {
      const message = requestSignal.didTimeout() ? 'Request timed out' : 'Network request failed';
      console.error('[ApiClient] Request failed', {
        method: 'POST',
        endpoint: getApiDiagnosticEndpoint(endpoint),
        errorKind: 'network',
        errorType: error instanceof Error ? error.name : 'Error',
      });
      return { success: false, error: message, errorKind: 'network' };
    } finally {
      requestSignal.cleanup();
    }
  }
}

export const api = new ApiClient(API_BASE_URL);

// Auth API (kept for backwards compatibility, but Clerk handles most auth now)
export const authApi = {
  // Get current user profile from D1 (works with both Clerk and legacy JWT)
  getMe: () =>
    api.get<{
      id: string;
      email: string;
      name: string;
      role: string;
      affiliate_id?: string;
      agent_id?: string;
      affiliate_name?: string;
      territory?: string;
    }>('/auth/me'),

  setRole: (data: {
    role: string;
    affiliate_name?: string;
    territory?: string;
    agent_code?: string;
  }) => api.post<{ success: boolean }>('/auth/set-role', data),

  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/auth/forgot-password', { email }, { skipAuth: true }),

  resetPassword: (token: string, password: string) =>
    api.post<{ message: string }>('/auth/reset-password', { token, password }, { skipAuth: true }),

  logout: () => api.post('/auth/logout'),
};

/**
 * Factory function to create an API client with a custom token getter.
 * Use this with useAuth().getToken from Clerk for reliable token retrieval.
 *
 * @example
 * const { getToken } = useAuth();
 * const apiClient = createApiClient(getToken);
 * const data = await apiClient.get('/endpoint');
 */
export function createApiClient(tokenGetter: TokenGetter, options: ApiClientOptions = {}) {
  return new ApiClient(API_BASE_URL, tokenGetter, options);
}

// Export types for consumers
export type { ApiResponse, TokenGetter };
